import { baseApi as api } from "../baseApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    postV2ProviderDocumentsEdit: build.mutation<
      PostV2ProviderDocumentsEditApiResponse,
      PostV2ProviderDocumentsEditApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/documents/edit`,
        method: "POST",
        body: queryArg.documentEditRequest,
      }),
    }),
    postV2ProviderDocumentsGeneratePdf: build.mutation<
      PostV2ProviderDocumentsGeneratePdfApiResponse,
      PostV2ProviderDocumentsGeneratePdfApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/documents/generate/pdf`,
        method: "POST",
        body: queryArg.generatePdfRequest,
      }),
    }),
    getV2ProviderDocumentsPrompts: build.query<
      GetV2ProviderDocumentsPromptsApiResponse,
      GetV2ProviderDocumentsPromptsApiArg
    >({
      query: () => ({ url: `/v2/provider/documents/prompts` }),
    }),
    postV2ProviderDocumentsPrompts: build.mutation<
      PostV2ProviderDocumentsPromptsApiResponse,
      PostV2ProviderDocumentsPromptsApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/documents/prompts`,
        method: "POST",
        body: queryArg.saveCustomPromptRequest,
      }),
    }),
    postV2ProviderDocumentsPromptsExtract: build.mutation<
      PostV2ProviderDocumentsPromptsExtractApiResponse,
      PostV2ProviderDocumentsPromptsExtractApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/documents/prompts/extract`,
        method: "POST",
        body: queryArg.extractDocumentPromptRequest,
      }),
    }),
    getV2ProviderDocumentsPromptsExtractByTaskId: build.query<
      GetV2ProviderDocumentsPromptsExtractByTaskIdApiResponse,
      GetV2ProviderDocumentsPromptsExtractByTaskIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/documents/prompts/extract/${queryArg.taskId}`,
      }),
    }),
    deleteV2ProviderDocumentsPromptsByPromptId: build.mutation<
      DeleteV2ProviderDocumentsPromptsByPromptIdApiResponse,
      DeleteV2ProviderDocumentsPromptsByPromptIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/documents/prompts/${queryArg.promptId}`,
        method: "DELETE",
      }),
    }),
    postV2ProviderDocumentsResume: build.mutation<
      PostV2ProviderDocumentsResumeApiResponse,
      PostV2ProviderDocumentsResumeApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/documents/resume`,
        method: "POST",
        body: queryArg.documentResumeRequest,
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type PostV2ProviderDocumentsEditApiResponse =
  /** status 200 OK */ DocumentEditResponse;
export type PostV2ProviderDocumentsEditApiArg = {
  documentEditRequest: DocumentEditRequest;
};
export type PostV2ProviderDocumentsGeneratePdfApiResponse =
  /** status 200 OK */ GeneratePdfResponse;
export type PostV2ProviderDocumentsGeneratePdfApiArg = {
  generatePdfRequest: GeneratePdfRequest;
};
export type GetV2ProviderDocumentsPromptsApiResponse =
  /** status 200 OK */ ListCustomPromptsResponse;
export type GetV2ProviderDocumentsPromptsApiArg = void;
export type PostV2ProviderDocumentsPromptsApiResponse =
  /** status 201 Created */ CustomPromptResponse2;
export type PostV2ProviderDocumentsPromptsApiArg = {
  saveCustomPromptRequest: SaveCustomPromptRequest;
};
export type PostV2ProviderDocumentsPromptsExtractApiResponse =
  /** status 202 Accepted */ ExtractPromptTaskResponse;
export type PostV2ProviderDocumentsPromptsExtractApiArg = {
  extractDocumentPromptRequest: ExtractDocumentPromptRequest;
};
export type GetV2ProviderDocumentsPromptsExtractByTaskIdApiResponse =
  /** status 200 OK */ ExtractPromptStatusResponse;
export type GetV2ProviderDocumentsPromptsExtractByTaskIdApiArg = {
  taskId: string;
};
export type DeleteV2ProviderDocumentsPromptsByPromptIdApiResponse =
  /** status 200 OK */ DeletePromptResponse;
export type DeleteV2ProviderDocumentsPromptsByPromptIdApiArg = {
  promptId: string;
};
export type PostV2ProviderDocumentsResumeApiResponse =
  /** status 200 OK */ DocumentEditResponse;
export type PostV2ProviderDocumentsResumeApiArg = {
  documentResumeRequest: DocumentResumeRequest;
};
export type DocumentEditResponse = {
  /** Processing status */
  status: string;
  /** URL to connect for SSE streaming */
  stream_url: string;
  /** Celery task ID for tracking */
  task_id: string;
  /** Thread ID for this operation */
  thread_id: string;
};
export type ClientErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the client error */
  status?: number;
};
export type ValidationErrorElement = {
  ctx?: object;
  loc: string[];
  msg: string;
  type: string;
};
export type ValidationError = ValidationErrorElement[];
export type ServerErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the server error */
  status?: number;
};
export type DocumentEditRequest = {
  /** Current document content in markdown */
  content?: string;
  /** Additional context (e.g., note_id for reference) */
  context?: {
    [key: string]: any;
  } | null;
  /** ID of custom prompt template to use for document generation */
  custom_prompt_id?: string | null;
  /** User's instruction or prompt (optional if using custom_prompt_id) */
  message?: string;
  /** Selected text for targeted editing */
  selected_text?: string | null;
  /** Optional thread ID for conversation context */
  thread_id?: string | null;
};
export type GeneratePdfResponse = {
  /** Base64-encoded PDF data */
  pdf_data: string;
};
export type GeneratePdfRequest = {
  /** Optional title for the document */
  document_title?: string | null;
  /** Complete markdown content to convert to PDF */
  markdown: string;
  /** Patient ID for tracking purposes */
  patient_id: string;
};
export type CustomPromptResponse = {
  /** When the prompt was created */
  created_at: string;
  /** Description of the template */
  description?: string | null;
  /** Type of document this creates */
  document_type: string;
  /** User-friendly name */
  name: string;
  /** UUID of the prompt */
  prompt_id: string;
  /** When the prompt was last updated */
  updated_at?: string | null;
};
export type ListCustomPromptsResponse = {
  /** Total number of prompts */
  count: number;
  /** List of custom document prompts */
  prompts?: CustomPromptResponse[];
};
export type CustomPromptResponse2 = {
  /** When the prompt was created */
  created_at: string;
  /** Description of the template */
  description?: string | null;
  /** Type of document this creates */
  document_type: string;
  /** User-friendly name */
  name: string;
  /** UUID of the prompt */
  prompt_id: string;
  /** When the prompt was last updated */
  updated_at?: string | null;
};
export type SaveCustomPromptRequest = {
  /** Description of what this template is for */
  description?: string | null;
  /** Type of document (e.g., 'discharge_summary') */
  document_type: string;
  /** User-friendly name for the template */
  name: string;
  /** The prompt for generating similar documents */
  prompt: string;
};
export type ExtractPromptTaskResponse = {
  /** Status message */
  message: string;
  /** Current status (queued) */
  status: string;
  /** Celery task ID for tracking */
  task_id: string;
  /** Thread ID being processed */
  thread_id: string;
};
export type ExtractDocumentPromptRequest = {
  /** Current document content in markdown */
  content: string;
  /** Additional context (e.g., note_id) */
  context?: {
    [key: string]: any;
  } | null;
  /** User's message explaining what to extract or context */
  message: string;
  /** Thread ID for conversation context */
  thread_id?: string | null;
};
export type ExtractDocumentPromptResponse = {
  /** Description of what this template creates */
  description: string;
  /** Type of document this creates */
  document_type: string;
  /** Suggested name for the template */
  name: string;
  /** The extracted reusable prompt */
  prompt: string;
  /** Generated UUID for the prompt */
  prompt_id: string;
};
export type ExtractPromptStatusResponse = {
  /** Error message if failed */
  error?: string | null;
  /** Status message */
  message?: string | null;
  /** Progress information if available */
  progress?: {
    [key: string]: any;
  } | null;
  /** Whether task has completed */
  ready: boolean;
  /** Extraction result if successful */
  result?: ExtractDocumentPromptResponse | null;
  /** Task state (PENDING, PROGRESS, SUCCESS, FAILURE) */
  status: string;
  /** Celery task ID */
  task_id: string;
  /** Error traceback if failed */
  traceback?: string | null;
};
export type DeletePromptResponse = {
  message?: string;
};
export type DocumentResumeRequest = {
  /** Data for resuming the workflow */
  resume_data?: {
    [key: string]: any;
  };
  /** Thread ID of interrupted workflow */
  thread_id: string;
};
